import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { HELP_CONTENT } from '@/lib/helpContent';

/**
 * ContextualHelp — Inline info popover for confusing UI elements.
 *
 * Usage:
 *   <ContextualHelp helpKey="stripe-sync">
 *     <span className="badge-amber">Sync</span>
 *   </ContextualHelp>
 *
 * Or standalone (no children — just the info icon):
 *   <ContextualHelp helpKey="prorated-charge" />
 */
export function ContextualHelp({
  helpKey,
  children,
  iconSize = 13,
}: {
  helpKey: string;
  children?: ReactNode;
  iconSize?: number;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const content = HELP_CONTENT[helpKey];
  if (!content) return <>{children}</>;

  useEffect(() => {
    if (!open) return;

    // Calculate position relative to trigger
    const trigger = triggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const popoverWidth = 280;
      let left = rect.left + rect.width / 2 - popoverWidth / 2;
      // Keep within viewport
      if (left < 8) left = 8;
      if (left + popoverWidth > window.innerWidth - 8) left = window.innerWidth - popoverWidth - 8;
      setPos({ top: rect.bottom + 8, left });
    }

    // Close on click-outside
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    // Close on Escape
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <button
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-surface-400 hover:text-brand-500 transition-colors flex-shrink-0"
        title="Help"
        type="button"
      >
        <HelpCircle size={iconSize} />
      </button>

      {open && pos && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[99999] animate-scale-in"
          style={{ top: pos.top, left: pos.left, width: 280 }}
        >
          <div className="bg-surface-0 border border-overlay/[0.08] shadow-elevated rounded-xl p-3.5">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <h4 className="text-xs font-semibold text-surface-900">{content.title}</h4>
              <button onClick={() => setOpen(false)} className="text-surface-400 hover:text-surface-600 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
            <p className="text-xs text-surface-600 leading-relaxed">{content.body}</p>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
